export type ThemeTokens = Record<string, string>;

export const THEME_IDS = ['warm-editorial', 'midnight', 'coastal'] as const;
export type ThemeId = (typeof THEME_IDS)[number];
export const DEFAULT_THEME_ID: ThemeId = 'warm-editorial';

// warm-editorial = current :root values from src/styles.css
const warmEditorial: ThemeTokens = {
  '--font-primary': "'Inter', system-ui, -apple-system, sans-serif",
  '--font-display': "'Playfair Display', serif",
  '--color-primary': '#005f73',
  '--color-secondary': '#0093b1',
  '--color-accent': '#00d4aa',
  '--color-background': '#f8f3ea',
  '--color-surface': '#fffdf8',
  '--color-surface-2': '#fbf5ea',
  '--color-text': '#2a241d',
  '--color-text-secondary': '#6b5d4a',
  '--color-warm': '#c2703f',
  '--color-warm-soft': '#e8c9a8',
  '--color-border': '#ece0cd',
  '--color-border-strong': '#e0d0b6',
  '--glass-bg': 'rgba(255, 253, 248, 0.92)',
  '--glass-border': 'rgba(224, 208, 182, 0.6)',
  '--shadow-sm': '0 2px 10px rgba(70, 55, 30, 0.06)',
  '--shadow-md': '0 6px 20px rgba(70, 55, 30, 0.09)',
  '--shadow-lg': '0 14px 40px rgba(70, 55, 30, 0.12)',
  '--shadow-xl': '0 24px 60px rgba(70, 55, 30, 0.16)',
};

// midnight = dark botanical "glass" look (reference image). Polished during Playwright pass.
const midnight: ThemeTokens = {
  '--font-primary': "'Inter', sans-serif",
  '--font-display': "'Playfair Display', serif",
  '--color-primary': '#8fb89a',
  '--color-secondary': '#6fae9a',
  '--color-accent': '#cBb27a',
  '--color-background': '#0f1f1a',
  '--color-surface': 'rgba(30, 50, 44, 0.55)',
  '--color-surface-2': 'rgba(40, 62, 55, 0.5)',
  '--color-text': '#ece7d9',
  '--color-text-secondary': '#a9b3a2',
  '--color-warm': '#c9a96b',
  '--color-warm-soft': '#e6d2a6',
  '--color-border': 'rgba(180, 200, 170, 0.18)',
  '--color-border-strong': 'rgba(180, 200, 170, 0.30)',
  '--glass-bg': 'rgba(22, 38, 33, 0.60)',
  '--glass-border': 'rgba(150, 200, 160, 0.25)',
  '--shadow-sm': '0 2px 12px rgba(0, 30, 22, 0.45)',
  '--shadow-md': '0 6px 28px rgba(0, 35, 25, 0.50)',
  '--shadow-lg': '0 16px 44px rgba(0, 40, 28, 0.55)',
  '--shadow-xl': '0 26px 64px rgba(0, 45, 30, 0.60)',
};

// coastal = clean, bright, airy. Polished during Playwright pass.
const coastal: ThemeTokens = {
  '--font-primary': "'Inter', sans-serif",
  '--font-display': "'Playfair Display', serif",
  '--color-primary': '#2b7a9b',
  '--color-secondary': '#3a9bbf',
  '--color-accent': '#11b3a3',
  '--color-background': '#f4f7f9',
  '--color-surface': '#ffffff',
  '--color-surface-2': '#eef3f6',
  '--color-text': '#1c2b33',
  '--color-text-secondary': '#5b6b73',
  '--color-warm': '#e08b5a',
  '--color-warm-soft': '#f3cdae',
  '--color-border': '#e2e8ec',
  '--color-border-strong': '#cdd8de',
  '--glass-bg': 'rgba(255, 255, 255, 0.92)',
  '--glass-border': 'rgba(205, 216, 222, 0.7)',
  '--shadow-sm': '0 2px 10px rgba(28, 43, 51, 0.06)',
  '--shadow-md': '0 6px 20px rgba(28, 43, 51, 0.10)',
  '--shadow-lg': '0 14px 40px rgba(28, 43, 51, 0.14)',
  '--shadow-xl': '0 24px 60px rgba(28, 43, 51, 0.18)',
};

export const THEMES: Record<ThemeId, ThemeTokens> = {
  'warm-editorial': warmEditorial,
  midnight,
  coastal,
};

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}
